import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getPlatformPromoCodes, createPlatformPromoCode, updatePlatformPromoCode } from '../../services/api';
import { formatDate } from '../../utils/formatters';

const TYPE_LABELS = { percent: 'Скидка, %', fixed: 'Скидка, ₽', trial_days: 'Доп. дней триала' };

const emptyForm = { code: '', discount_type: 'percent', discount_value: '', max_uses: '', valid_until: '' };

// Простые промокоды (см. план: без реферальной программы) — процент/фикс
// скидки на оплату (checkout/invoice) либо продление триала при регистрации.
const PromoCodesCard = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPlatformPromoCodes();
      setCodes(data.promo_codes || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить промокоды');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      const data = await createPlatformPromoCode({
        code: form.code, discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses === '' ? null : Number(form.max_uses),
        valid_until: form.valid_until || null,
      });
      setCodes(data.promo_codes || []);
      setForm(emptyForm);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Не удалось создать промокод');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (promo) => {
    const data = await updatePlatformPromoCode(promo.id, { active: !promo.active });
    setCodes(data.promo_codes || []);
  };

  return (
    <section className="ui-card">
      <div className="ui-card-head">
        <h2>Промокоды</h2>
        <p>Продление триала — только при регистрации; скидка на оплату — при чекауте картой или счёте.</p>
      </div>

      <form className="ui-form" onSubmit={handleCreate}>
        <div className="ui-grid">
          <div className="ui-field">
            <label>Код</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="SAVE20" required
            />
          </div>
          <div className="ui-field">
            <label>Тип</label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
              <option value="percent">{TYPE_LABELS.percent}</option>
              <option value="fixed">{TYPE_LABELS.fixed}</option>
              <option value="trial_days">{TYPE_LABELS.trial_days}</option>
            </select>
          </div>
          <div className="ui-field">
            <label>Размер</label>
            <input
              type="number" min="0" step="1"
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              placeholder={form.discount_type === 'percent' ? '20' : form.discount_type === 'trial_days' ? '7' : '500'}
              required
            />
          </div>
          <div className="ui-field">
            <label>Лимит использований (пусто = без лимита)</label>
            <input
              type="number" min="1" step="1"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              placeholder="без лимита"
            />
          </div>
          <div className="ui-field">
            <label>Действует до (пусто = бессрочно)</label>
            <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
          </div>
        </div>
        {formError && <div className="ui-inline-error">{formError}</div>}
        <div className="ui-form-actions">
          <button type="submit" className="ui-btn-primary" disabled={saving}>
            {saving ? 'Создаём…' : 'Создать промокод'}
          </button>
        </div>
      </form>

      {loading && <LoadingSpinner />}
      {!loading && error && <div className="ui-inline-error">{error}</div>}
      {!loading && !error && codes.length === 0 && <div className="ui-empty">Промокодов пока нет</div>}
      {!loading && !error && codes.length > 0 && (
        <div className="platform-org-table">
          {codes.map((promo) => (
            <div className="platform-org-row" key={promo.id}>
              <div className="platform-org-main">
                <div className="platform-org-name">{promo.code}</div>
                <div className="ui-dim">
                  {TYPE_LABELS[promo.discount_type]}: {promo.discount_value}
                  {promo.valid_until && ` · до ${formatDate(promo.valid_until)}`}
                </div>
              </div>
              <span className="ui-badge">
                {promo.used_count}{promo.max_uses ? ` / ${promo.max_uses}` : ''} использований
              </span>
              <span className={`ui-status ${promo.active ? 'on' : 'off'}`}>{promo.active ? 'Активен' : 'Выключен'}</span>
              <div className="platform-org-actions">
                <button type="button" className="ui-btn-ghost" onClick={() => handleToggle(promo)}>
                  {promo.active ? 'Выключить' : 'Включить'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PromoCodesCard;
